import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import Swal from 'sweetalert2';
import { useSelectedLocation } from './LocationContext';
import AdminLayout from './AdminLayout';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const CROSSOVER_OPTIONS = [
  { value: 'start_day', label: 'Assign all hours to the day the shift began' },
  { value: 'split_midnight', label: 'Split hours exactly at 12:00 AM' },
];

const DEFAULT_RULES = {
  startOfWeek: 0,
  crossoverShiftType: 'start_day',
  maxRegDaily: 8,
  maxRegWeekly: 40,
  maxOtDaily: 12,
  multReg: 1.0,
  multOt: 1.5,
  multDt: 2.0,
  consecutive7thDay: false,
  otOnHolidays: false,
  restPeriodViolation: false,
};

const RulesPage = ({ embedded = false }) => {
  const [user, setUser] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ ...DEFAULT_RULES });
  const [hasExistingRule, setHasExistingRule] = useState(false);

  const auth = getAuth();
  const { selectedLocation } = useSelectedLocation() || {};

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadLocations(currentUser.uid);
      }
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When navbar location changes, select it
  useEffect(() => {
    if (selectedLocation?.id && locations.length > 0) {
      setSelectedLocationId(selectedLocation.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, locations.length]);

  // Load rule when location selection changes
  useEffect(() => {
    if (user && selectedLocationId) {
      loadRule(user.uid, selectedLocationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedLocationId]);

  const loadLocations = async (uid) => {
    try {
      const db = getFirestore();
      const snapshot = await getDocs(collection(db, 'users', uid, 'locations'));
      const locs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setLocations(locs);
      // Default to navbar-selected location, or first location
      const defaultLocId = selectedLocation?.id || (locs.length > 0 ? locs[0].id : '');
      setSelectedLocationId(defaultLocId);
    } catch (e) {
      console.error('Error loading locations:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadRule = async (uid, locationId) => {
    if (!locationId) return;
    setLoading(true);
    try {
      const db = getFirestore();
      const ruleDoc = await getDoc(doc(db, 'users', uid, 'locationRules', locationId));
      if (ruleDoc.exists()) {
        const data = ruleDoc.data();
        setFormData({
          startOfWeek: data.startOfWeek ?? DEFAULT_RULES.startOfWeek,
          crossoverShiftType: data.crossoverShiftType ?? DEFAULT_RULES.crossoverShiftType,
          maxRegDaily: data.maxRegDaily ?? DEFAULT_RULES.maxRegDaily,
          maxRegWeekly: data.maxRegWeekly ?? DEFAULT_RULES.maxRegWeekly,
          maxOtDaily: data.maxOtDaily ?? DEFAULT_RULES.maxOtDaily,
          multReg: data.multReg ?? DEFAULT_RULES.multReg,
          multOt: data.multOt ?? DEFAULT_RULES.multOt,
          multDt: data.multDt ?? DEFAULT_RULES.multDt,
          consecutive7thDay: data.consecutive7thDay ?? DEFAULT_RULES.consecutive7thDay,
          otOnHolidays: data.otOnHolidays ?? DEFAULT_RULES.otOnHolidays,
          restPeriodViolation: data.restPeriodViolation ?? DEFAULT_RULES.restPeriodViolation,
        });
        setHasExistingRule(true);
      } else {
        setFormData({ ...DEFAULT_RULES });
        setHasExistingRule(false);
      }
    } catch (e) {
      console.error('Error loading rule:', e);
      Swal.fire('Error', 'Failed to load rule settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user || !selectedLocationId) {
      Swal.fire('Error', 'Please select a location', 'error');
      return;
    }
    setSaving(true);
    try {
      const db = getFirestore();
      await setDoc(doc(db, 'users', user.uid, 'locationRules', selectedLocationId), {
        ...formData,
        locationId: selectedLocationId,
        updatedAt: serverTimestamp(),
        ...(hasExistingRule ? {} : { createdAt: serverTimestamp() }),
      }, { merge: true });
      setHasExistingRule(true);
      Swal.fire({
        title: 'Saved!',
        text: `Overtime rules for ${getLocationName(selectedLocationId)} have been saved.`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (e) {
      console.error('Error saving rule:', e);
      Swal.fire('Error', 'Failed to save rules', 'error');
    } finally {
      setSaving(false);
    }
  };

  const getLocationName = (id) => {
    const loc = locations.find(l => l.id === id);
    return loc ? loc.companyName || loc.locationName : 'Unknown';
  };

  const SectionCard = ({ title, icon, children }) => (
    <div className="card mb-4" style={{ borderRadius: 16, border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
      <div className="card-body">
        <h6 className="font-weight-bold mb-3" style={{ color: '#333' }}>
          <i className={`fas ${icon} mr-2 text-primary`}></i>{title}
        </h6>
        {children}
      </div>
    </div>
  );

  const ToggleRow = ({ label, description, checked, onChange }) => (
    <div className="d-flex align-items-center justify-content-between py-2" style={{ borderBottom: '1px solid #f0f0f0' }}>
      <div>
        <div className="font-weight-bold" style={{ fontSize: '0.9rem' }}>{label}</div>
        <small className="text-muted" style={{ fontSize: '0.8rem' }}>{description}</small>
      </div>
      <div className="custom-control custom-switch">
        <input
          type="checkbox"
          className="custom-control-input"
          id={`toggle-${label}`}
          checked={checked}
          onChange={e => onChange(e.target.checked)}
        />
        <label className="custom-control-label" htmlFor={`toggle-${label}`}></label>
      </div>
    </div>
  );

  const NumberField = ({ label, value, onChange, min, max, step = 1, suffix = '' }) => (
    <div className="form-group mb-3">
      <label className="font-weight-bold" style={{ fontSize: '0.85rem' }}>{label}</label>
      <div className="input-group" style={{ maxWidth: 160 }}>
        <input
          type="number"
          className="form-control"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={e => onChange(parseFloat(e.target.value))}
        />
        {suffix && (
          <div className="input-group-append">
            <span className="input-group-text">{suffix}</span>
          </div>
        )}
      </div>
    </div>
  );

  const content = (
    <div className="rules-page">
      {/* Header */}
      <div className="row mb-4 align-items-center">
        <div className="col-md-6">
          <h4 className="font-weight-bold">Overtime Rules</h4>
          <p className="text-muted">Configure payroll and overtime settings per location</p>
        </div>
        <div className="col-md-6 text-md-right">
          <div className="d-inline-block" style={{ minWidth: 260 }}>
            <label className="small text-muted mb-1 d-block text-left">Location</label>
            <select
              className="form-control"
              value={selectedLocationId}
              onChange={e => setSelectedLocationId(e.target.value)}
              disabled={loading}
            >
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>
                  {loc.companyName || loc.locationName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Location banner */}
      {selectedLocationId && (
        <div className="card mb-4" style={{ borderRadius: 16, border: 'none', background: '#fff8f5' }}>
          <div className="card-body py-3 d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div
                className="d-flex align-items-center justify-content-center mr-3"
                style={{ width: 40, height: 40, borderRadius: 10, background: '#ff6b35' }}
              >
                <i className="fas fa-store text-white"></i>
              </div>
              <div>
                <div className="font-weight-bold" style={{ fontSize: '1.1rem' }}>
                  {getLocationName(selectedLocationId)}
                </div>
                <small className="text-muted">
                  {hasExistingRule ? 'Rules configured for this location' : 'No rules saved yet — defaults shown'}
                </small>
              </div>
            </div>
            <button
              className="btn btn-primary font-weight-bold"
              style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', borderRadius: 12, minWidth: 120 }}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <span className="spinner-border spinner-border-sm mr-2"></span>
              ) : (
                <i className="fas fa-save mr-2"></i>
              )}
              Save Rules
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
        </div>
      ) : (
        <>
          <div className="row">
            <div className="col-lg-6">
              {/* Time Modifiers */}
              <SectionCard title="Time Modifiers (Calendar Structure)" icon="fa-calendar-alt">
                <div className="form-group mb-3">
                  <label className="font-weight-bold" style={{ fontSize: '0.85rem' }}>Start of Week</label>
                  <p className="text-muted small mb-2">Defines the day your 7-day payroll cycle begins</p>
                  <select
                    className="form-control"
                    value={formData.startOfWeek}
                    onChange={e => setFormData({ ...formData, startOfWeek: parseInt(e.target.value) })}
                    style={{ maxWidth: 240 }}
                  >
                    {DAYS_OF_WEEK.map(d => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group mb-0">
                  <label className="font-weight-bold" style={{ fontSize: '0.85rem' }}>Crossover Shift Handling</label>
                  <p className="text-muted small mb-2">How hours are counted when a shift crosses midnight</p>
                  <select
                    className="form-control"
                    value={formData.crossoverShiftType}
                    onChange={e => setFormData({ ...formData, crossoverShiftType: e.target.value })}
                  >
                    {CROSSOVER_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </SectionCard>

              {/* Threshold Modifiers */}
              <SectionCard title="Threshold Modifiers (Hour Limits)" icon="fa-hourglass-half">
                <div className="row">
                  <div className="col-md-4">
                    <NumberField
                      label="Max Regular (Daily)"
                      value={formData.maxRegDaily}
                      onChange={v => setFormData({ ...formData, maxRegDaily: v })}
                      min={1}
                      max={24}
                      suffix="h"
                    />
                  </div>
                  <div className="col-md-4">
                    <NumberField
                      label="Max Regular (Weekly)"
                      value={formData.maxRegWeekly}
                      onChange={v => setFormData({ ...formData, maxRegWeekly: v })}
                      min={1}
                      max={168}
                      suffix="h"
                    />
                  </div>
                  <div className="col-md-4">
                    <NumberField
                      label="Max Overtime (Daily)"
                      value={formData.maxOtDaily}
                      onChange={v => setFormData({ ...formData, maxOtDaily: v })}
                      min={1}
                      max={24}
                      suffix="h"
                    />
                  </div>
                </div>
                <div className="alert alert-light border mt-2 mb-0" style={{ borderRadius: 10, fontSize: '0.8rem' }}>
                  <i className="fas fa-info-circle mr-1 text-info"></i>
                  <strong>California defaults:</strong> Daily regular = 8h, Weekly regular = 40h, Daily OT limit = 12h.
                  <strong> Federal defaults:</strong> Daily regular = N/A, Weekly regular = 40h.
                </div>
              </SectionCard>
            </div>

            <div className="col-lg-6">
              {/* Multiplier Modifiers */}
              <SectionCard title="Multiplier Modifiers (Pay Rates)" icon="fa-percentage">
                <div className="row">
                  <div className="col-md-4">
                    <NumberField
                      label="Regular Multiplier"
                      value={formData.multReg}
                      onChange={v => setFormData({ ...formData, multReg: v })}
                      min={0.5}
                      max={10}
                      step={0.1}
                      suffix="x"
                    />
                  </div>
                  <div className="col-md-4">
                    <NumberField
                      label="Overtime Multiplier"
                      value={formData.multOt}
                      onChange={v => setFormData({ ...formData, multOt: v })}
                      min={0.5}
                      max={10}
                      step={0.1}
                      suffix="x"
                    />
                  </div>
                  <div className="col-md-4">
                    <NumberField
                      label="Double-Time Multiplier"
                      value={formData.multDt}
                      onChange={v => setFormData({ ...formData, multDt: v })}
                      min={0.5}
                      max={10}
                      step={0.1}
                      suffix="x"
                    />
                  </div>
                </div>
                <div className="alert alert-light border mt-3 mb-0" style={{ borderRadius: 10, fontSize: '0.8rem' }}>
                  <i className="fas fa-info-circle mr-1 text-info"></i>
                  Standard multipliers: Regular = 1.0x, Overtime = 1.5x, Double-Time = 2.0x.
                </div>
              </SectionCard>

              {/* Special Rule Modifiers */}
              <SectionCard title="Special Rule Modifiers" icon="fa-sliders-h">
                <ToggleRow
                  label="7th Consecutive Day Penalty"
                  description="Activates extra pay penalty for working all 7 days of the workweek (California rule)"
                  checked={formData.consecutive7thDay}
                  onChange={v => setFormData({ ...formData, consecutive7thDay: v })}
                />
                <ToggleRow
                  label="OT on Holidays"
                  description="Official holidays are automatically paid as Overtime from the first hour"
                  checked={formData.otOnHolidays}
                  onChange={v => setFormData({ ...formData, otOnHolidays: v })}
                />
                <ToggleRow
                  label="Rest Period Violation"
                  description="Penalize with +1 hour of extra pay if minimum rest between shifts is not observed"
                  checked={formData.restPeriodViolation}
                  onChange={v => setFormData({ ...formData, restPeriodViolation: v })}
                />
              </SectionCard>
            </div>
          </div>
        </>
      )}
    </div>
  );

  if (embedded) return content;
  return (
    <AdminLayout title="Rules">
      {content}
    </AdminLayout>
  );
};

export default RulesPage;
